/** @format */

import {
  AnalyticsService,
  UtilityService,
  WINDOW,
  LocalStorageService,
  Logger,
} from '@shared/data-access/services';
import {
  AutoSelectTrueFitContext,
  UserProfileStateTrueFitContext,
  AttributeOption,
} from '@pdp/data-access/interfaces';
import { Environment, ProductSku, TagIDs } from '@shared/data-access/interfaces';
import { DOCUMENT, Injectable, inject } from '@angular/core';
import { Observable, fromEvent, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectKillswitch } from '@pdp/data-access/state';
import { startWith, take, map } from 'rxjs/operators';
import { trueFitRecommendedSizeSelected } from '../+state/attributes.actions';
import { AuthenticationService } from '@shared/authentication/data-access';

interface AvailableAttributeOptions {
  selectableShoeWidthAttributes?: AttributeOption[];
  selectableSizeAttributes?: AttributeOption[];
}

@Injectable({
  providedIn: 'root',
})
export class AttributesTrueFitService {
  private readonly utilityService = inject(UtilityService);
  private readonly environment = inject(Environment);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly window = inject(WINDOW);
  private readonly document = inject(DOCUMENT);
  private readonly ngRxStore = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly authService = inject(AuthenticationService);
  private readonly logger = inject(Logger);
  private readonly trueFitExperimentsEnabled = this.ngRxStore.selectSignal(
    selectKillswitch('trueFitExperimentsEnabled'),
  );

  tfScriptLoaded = false;
  tfScriptReady = false;
  tfSelColor: string;
  /**
   * @description Loads truefit script and sets up recommended size for this product if truefit was used before.
   */
  loadTruefitScriptIfEnabled(
    attributes$: Observable<AttributeOption[][]>,
    trueFitSelectedColor: string,
    productId: string,
    productSkusData: ProductSku[] | null,
  ): void {
    this.tfSelColor = trueFitSelectedColor;
    // Get current available sizes
    const sizeWidthValues = this.getAvailableAttributeOptions(attributes$);
    if (sizeWidthValues.selectableSizeAttributes) {
      // Load truefit script if product has color attribute
      const fnLoadTrueFit = () => {
        if (!this.tfScriptLoaded) {
          this.utilityService.loadExternalScript(
            `${this.environment.thirdParty.trueFit.baseUrl}`,
            () => {
              this.truefitUserProfileState();
              this.truefitOpenModelEvent();

              this.updateAttributes(attributes$, productId, productSkusData);

              // True Fit Experiments Event
              this.truefitExperimentsEvent();
              this.tfScriptReady = true;
            },
          );
          this.tfScriptLoaded = true;
        } else if (this.tfScriptReady) {
          this.updateAttributes(attributes$, productId, productSkusData);
        }
      };

      this.utilityService.runWhenIdle(fnLoadTrueFit);
    }
  }

  updateAttributes(
    attributes$: Observable<AttributeOption[][]>,
    productId: string,
    productSkusData: ProductSku[] | null,
  ) {
    const sizeWidthValues = this.getAvailableAttributeOptions(attributes$);
    // sizes in string format so trufit can calculate.
    const sizesString = this.setSizeAndWidthStringForTruefit(sizeWidthValues);

    this.getAthleteID$()
      .pipe(take(1))
      .subscribe((userId) => {
        this.calculateTrueFit(
          this.tfSelColor,
          sizesString,
          userId,
          productId,
          attributes$,
          productSkusData,
        );
      });
  }

  /**
   * @description Checks if the attribute name has size in it.
   * @return Returns a boolean representing if the attribute name has size in it.
   */
  isAttributeSize(attributeName: string): boolean {
    return attributeName.toLowerCase().includes('size');
  }

  /**
   * @description Emits the athlete id from the TAG_IDs local storage key when it becomes available.
   * If the athlete is not authenticated, it always returns an empty string, since utag does not
   * clear TAG_IDs when the athlete logs out.
   */
  getAthleteID$() {
    // `CustomerIDApiUsed` is fired by utag once AthleteID becomes populated, but only if it wasn't already populated on page load
    const eventTrigger$ = fromEvent(this.document, 'CustomerIDApiUsed').pipe(startWith(null));
    return combineLatest([eventTrigger$, this.authService.getIsAuthenticated$()]).pipe(
      map(([, isAuthenticated]) => {
        if (!isAuthenticated) {
          return '';
        }
        const tagIDString = this.localStorageService.get('TAG_IDs');
        try {
          const tagIDs: TagIDs | null | undefined = tagIDString
            ? JSON.parse(tagIDString)
            : tagIDString;
          return tagIDs?.AthleteID?.ID || '';
        } catch (error) {
          this.logger.error(
            `Failed to parse TAG_ID string "${tagIDString}" from local storage.`,
            error,
          );
          return '';
        }
      }),
    );
  }

  /**
   * @description Goes through all the available sizing options for this product and creates a string of all available sizes/widths.
   * @returns A string with the format "6.0 Medium/D:6.5 Medium/D:7.0 Medium/D" (if shoe width is present), if not the format will just be "6.0:6.5:7.0"
   */
  private setSizeAndWidthStringForTruefit(sizeWidthValues: AvailableAttributeOptions): string {
    const selectableSizes: string[] = [];
    // If product contains shoe width append the string Ex 11.0 Medium/D
    if (
      sizeWidthValues.selectableShoeWidthAttributes !== undefined &&
      sizeWidthValues.selectableShoeWidthAttributes.length > 0
    ) {
      for (const size of sizeWidthValues.selectableSizeAttributes) {
        for (const shoeWidth of sizeWidthValues.selectableShoeWidthAttributes) {
          selectableSizes.push(`${size.value} ${shoeWidth.value}`);
        }
      }
    } else {
      for (const size of sizeWidthValues.selectableSizeAttributes) {
        selectableSizes.push(`${size.value}`);
      }
    }

    return selectableSizes.join(':');
  }

  /**
   * @description Retrieves all the available sizes and shoe widths of this product from the store.
   * @returns Returns an object with all the available sizes and shoe widths.
   */
  private getAvailableAttributeOptions(
    attributes$: Observable<AttributeOption[][]>,
  ): AvailableAttributeOptions {
    let selectableSizeAttributes: AttributeOption[];
    let selectableShoeWidthAttributes: AttributeOption[];
    attributes$.pipe(take(1)).subscribe((allAttributeOptions) => {
      allAttributeOptions.forEach((attributeOption) => {
        if (attributeOption[0]?.name.includes('Size')) {
          selectableSizeAttributes = attributeOption;
        } else if (attributeOption[0]?.name === 'Shoe Width') {
          selectableShoeWidthAttributes = attributeOption;
        }
      });
      return { selectableSizeAttributes, selectableShoeWidthAttributes };
    });
    return { selectableSizeAttributes, selectableShoeWidthAttributes };
  }

  private truefitUserProfileState(): void {
    if (this.window['tfcapi']) {
      this.window['tfcapi'](
        'event',
        'tfc-fitrec-product',
        'render',
        (context: UserProfileStateTrueFitContext) => {
          const fitRecStatus = context.response.fitRecommendations[0]?.status;
          this.analyticsService.setEventData({ status: fitRecStatus });
          // Trigger the Analytics update
          this.analyticsService.triggerAnalyticsEvent('TFprofileStatus');
        },
      );
    }
  }

  private truefitExperimentsEvent(): void {
    if (this.window['tfcapi'] && this.trueFitExperimentsEnabled()) {
      this.window['tfcapi'](
        'event',
        'tfc-fitrec-product',
        'experiments',
        (experiments: Record<string, unknown>) => {
          const experiment = Object.entries(experiments).map(([key, value]) => ({
            key,
            value,
          }));
          if (experiment[0]?.value) {
            this.analyticsService.triggerAnalyticsEvent('multiUseAnalyticsEvent', {
              event: 'TrueFitUniversalGuidance',
              value: experiment[0].value,
            });
          }
        },
      );
    }
  }

  private truefitOpenModelEvent() {
    if (this.window['tfcapi']) {
      this.window['tfcapi']('event', 'tfc-fitrec-register', 'open', () => {
        /*
         * Analytics Event
         */
        // Set the modal name
        this.analyticsService.setModalName('Truefit');

        // Set the Analytics event type
        this.analyticsService.setType('event', 'ModalOpen');

        // Trigger the Analytics update
        this.analyticsService.triggerAnalyticsEvent('ModalOpen');
      });
    }
  }

  /**
   * @description Makes a call to the truefit script to calculate the recommended size given
   * the color, available sizes, user id, and product id.
   */
  private calculateTrueFit(
    colorId: string,
    availableSizes: string,
    userId: string,
    productId: string,
    attributes$: Observable<AttributeOption[][]>,
    productSkuData: ProductSku[] | null,
  ): void {
    // Calculate truefit event hook
    this.window['tfcapi']('calculate', {
      userId,
      products: {
        [productId]: {
          colorId,
          availableSizes,
        },
      },
    });
    // Listen for true fit size selection events
    this.sizeAutoSelectTrueFit(attributes$, productSkuData);
  }

  /**
   * @description Auto selects the sizes recommended by truefit if available.
   */
  private sizeAutoSelectTrueFit(
    attributes$: Observable<AttributeOption[][]>,
    productSkuData: ProductSku[] | null,
  ) {
    // Call site-specific hook to select the recommended size
    this.window['tfcapi'](
      'event',
      'tfc-fitrec-product',
      'success',
      (context: AutoSelectTrueFitContext) => {
        // Select size attributes if possible
        // Examples of truefit recommended size inputs
        // 11.0
        // 11.0 Medium/D
        // Get recommended size and check for secondary size such as shoe width
        const recommendedSize: string[] = context.fitRecommendation.size.split(' ');

        // Get current available sizes
        const sizeWidthValues = this.getAvailableAttributeOptions(attributes$);

        // Select size attribute if possible
        this.isRecommendedSizeSelectable(
          sizeWidthValues.selectableSizeAttributes,
          recommendedSize[0],
          productSkuData,
        );

        // Select secondary attribute if possible
        if (recommendedSize.length > 1) {
          this.isRecommendedSizeSelectable(
            sizeWidthValues.selectableShoeWidthAttributes,
            recommendedSize[1],
            productSkuData,
          );
        }
      },
    );
  }

  /**
   * @description Filters through the selectable size attributes and checks if recommendedSize is available and selects it.
   */
  private isRecommendedSizeSelectable(
    selectableAttributes: AttributeOption[],
    recommendedSize: string,
    productSkuData: ProductSku[],
  ): void {
    const selectedRecommendedSize = selectableAttributes.find(
      (attribute) => attribute.value === recommendedSize,
    );

    if (selectedRecommendedSize) {
      this.ngRxStore.dispatch(
        trueFitRecommendedSizeSelected({
          allProductSkus: productSkuData,
          selectedAttribute: {
            name: selectedRecommendedSize.name,
            value: selectedRecommendedSize.value,
          },
        }),
      );
    }
  }
}
